import {
  MAX_REPORT_NOTE,
  REPORT_REASON_LABELS,
  REVIEW_COPY,
  REVIEW_REPORT_TURNSTILE_ACTION,
  reportDraftProblem,
  VISITOR_REPORT_REASONS,
  type VisitorReportReason,
} from "@fusiqo/vozqi-shared";
import { useEffect, useRef, useState } from "react";

import { getOpsTurnstileSitekey } from "../lib/config";
import { useTurnstile } from "../platform/turnstile/useTurnstile";
import { useReviewReport } from "./use-review-report";

/** What the press finds missing, in the order the form asks it; null when it may go. */
function problemOf(reason: VisitorReportReason | null, note: string, email: string, needsToken: boolean): string | null {
  const draft = reportDraftProblem({ reason, note });
  if (draft !== null) return draft;
  if (email !== "" && !email.includes("@")) return REVIEW_COPY.reportEmailInvalid;
  return needsToken ? REVIEW_COPY.reportCaptcha : null;
}

/**
 * «DENUNCIAR RESEÑA» on the public profile (P3 Enmienda 4): a reason, a note,
 * an email if the visitor wants an answer, and the /ops login's Turnstile
 * widget. A native <dialog>, no kit, as the photo viewer. Every check is said
 * on the press; the button is never grey. Once sent, the thanks take the
 * form's place and ✕ closes it.
 */
export function ReviewReportDialog({ slug, reviewId, onClose }: { slug: string; reviewId: string; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const sitekey = getOpsTurnstileSitekey();
  const turnstile = useTurnstile({ sitekey, action: REVIEW_REPORT_TURNSTILE_ACTION });
  const report = useReviewReport(slug, reviewId, { on: sitekey !== undefined, reset: turnstile.reset });
  const [reason, setReason] = useState<VisitorReportReason | null>(null);
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const element = dialog.current;
    if (element !== null && !element.open) element.showModal();
  }, []);

  const press = (): void => {
    const typed = email.trim();
    const problem = problemOf(reason, note, typed, sitekey !== undefined && turnstile.token === null);
    if (reason === null || problem !== null) {
      // A widget whose script never loaded will not hand a token however long the visitor waits.
      const blocked = problem === REVIEW_COPY.reportCaptcha && turnstile.failed;
      report.refuse(blocked ? REVIEW_COPY.reportFailed : (problem ?? REVIEW_COPY.reasonRequired));
      return;
    }
    report.send({ reason, note: note.trim(), email: typed === "" ? null : typed }, turnstile.token);
  };

  return (
    <dialog
      ref={dialog}
      className="review-report"
      aria-labelledby="review-report-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="review-report-head">
        <h2 id="review-report-title">{REVIEW_COPY.reportTitle}</h2>
        <button type="button" className="review-report-close" aria-label="Cerrar" onClick={onClose}>
          ✕
        </button>
      </div>
      {report.sent ? (
        <p className="review-report-sent" role="status">
          {REVIEW_COPY.reportSent}
        </p>
      ) : (
        <form
          className="review-report-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            press();
          }}
        >
          <fieldset className="review-report-reasons">
            {VISITOR_REPORT_REASONS.map((value) => (
              <label key={value}>
                <input type="radio" name="reason" value={value} checked={reason === value} onChange={() => setReason(value)} />
                {REPORT_REASON_LABELS[value]}
              </label>
            ))}
          </fieldset>
          <label className="review-report-field">
            {reason === "other" ? REVIEW_COPY.otherNoteLabel : REVIEW_COPY.reportNoteLabel}
            <textarea rows={3} maxLength={MAX_REPORT_NOTE} value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <label className="review-report-field">
            {REVIEW_COPY.reportEmailLabel}
            <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          {sitekey === undefined ? null : <div ref={turnstile.ref} className="review-report-widget" />}
          {report.problem === null ? null : (
            <p className="review-report-problem" role="alert">
              {report.problem}
            </p>
          )}
          <button type="submit" className="review-report-send" aria-busy={report.sending}>
            {REVIEW_COPY.reportSend}
          </button>
        </form>
      )}
    </dialog>
  );
}
